import { parseISO } from 'date-fns';

import { Endpoints, ScopedBoolean } from '@/api';
import { getRefashionStatus, isCustom as isActionCustom } from '@/models/actionType';
import {
  ArticleConditionForm,
  ConditionForm,
  ConditionFormData,
  getArticlePhoto,
  isMissingCustomsData,
} from '@/models/article';
import { isCustom as isDefectCustom } from '@/models/defectType';
import { Medium, MediumFromGetMany } from '@/models/medium';
import { hasColorOptions } from '@/models/product';
import { ProductAttribute } from '@/models/productAttribute';
import {
  ArticleWithRelations,
  isRequiredPaidEstimateAdvanceDeposit,
  RequestWithRelations,
} from '@/models/request';
import { hasSizeOptions } from '@/models/sizeProduct';
import { hasPermission, UserFromGetSession } from '@/models/user';
import { getScopedBoolean as getWorkflowScopedBoolean } from '@/models/workflow';

import { getSubQuestion } from '../New/components/ConditionForm/ConditionQuestion';

import { ArticleContextData } from './ArticleContext';
import { RequestContextData } from './interfaces';

export function computeRequestErrors({
  request,
  currentSession,
  proofOfPurchaseMedia,
  proofOfAuthenticityMedia,
  initialConditionFormsData,
  qualityControlFormsData,
  productAttributesData,
  requestView,
  articleState,
  depositOptions,
}: {
  request: RequestWithRelations;
  currentSession: UserFromGetSession;
  proofOfPurchaseMedia: MediumFromGetMany[];
  proofOfAuthenticityMedia: MediumFromGetMany[];
  initialConditionFormsData?: { articleId: string; formData?: ConditionFormData }[];
  qualityControlFormsData?: { articleId: string; formData?: ConditionFormData }[];
  productAttributesData?: {
    articleId: string;
    productAttributes?: ProductAttribute[];
  }[];
  requestView: RequestContextData['view'];
  articleState?: Omit<ArticleContextData['state'], 'errors'>;
  depositOptions?: Endpoints['GET /requests/:id/deposit-options']['response'];
}) {
  const getScopedBoolean = (scopedBoolean: ScopedBoolean | undefined) => {
    return getWorkflowScopedBoolean({
      scopedBoolean,
      credentialsScopes: request.scopes,
    });
  };

  return {
    information: {
      missingSalesAdvisor: !request.salesAdvisor,
      missingCustomFieldValues: requestView.information.customFields.map((customField) => {
        const customFieldValue = request.customFieldValues?.find(
          (customFieldValue) => customFieldValue.id === customField.id
        );

        return {
          id: customField.id,
          missingValue:
            getScopedBoolean(customField.required) &&
            (!customFieldValue || !customFieldValue.value?.trim()),
        };
      }),
    },
    articles: {
      // We only need to compute the errors of active articles
      articles: request.articles.map((article) => ({
        id: article.id,
        ...computeArticleErrors({
          article,
          currentSession,
          proofOfPurchaseMedia: proofOfPurchaseMedia.filter(
            (media) => media.articleId === article.id
          ),
          proofOfAuthenticityMedia: proofOfAuthenticityMedia.filter(
            (media) => media.articleId === article.id
          ),
          initialConditionFormData: initialConditionFormsData?.find(
            ({ articleId }) => articleId === article.id
          )?.formData,
          qualityControlFormData: qualityControlFormsData?.find(
            ({ articleId }) => articleId === article.id
          )?.formData,
          productAttributes: productAttributesData?.find(
            ({ articleId }) => articleId === article.id
          )?.productAttributes,
          requestView,
          articleState,
        }),
      })),
      noArticles: request.articles.length === 0,
    },
    price: {
      missingWaiveHandlingFeeReason:
        !!request.waiveHandlingFee && !request.waiveHandlingFeeReason?.reason,
      missingWaiveHandlingFeeOtherReason:
        !!request.waiveHandlingFee &&
        request.waiveHandlingFeeReason?.reason === 'other' &&
        !request.waiveHandlingFeeReason.otherReason,
      missingDepositChoice:
        !!depositOptions &&
        !isRequiredPaidEstimateAdvanceDeposit(depositOptions) &&
        !request.deposits[0],
    },
  };
}

export type RequestErrors = ReturnType<typeof computeRequestErrors>;
export type RequestErrorsFilter = ErrorsFilter<RequestErrors>;
export type FilteredRequestErrors = FilteredErrors<RequestErrors>;

export function computeArticleErrors({
  article,
  currentSession,
  proofOfPurchaseMedia,
  proofOfAuthenticityMedia,
  initialConditionFormData,
  qualityControlFormData,
  productAttributes = [],
  requestView,
  articleState,
}: {
  article: ArticleWithRelations;
  currentSession: UserFromGetSession;
  proofOfPurchaseMedia: Medium[];
  proofOfAuthenticityMedia: Medium[];
  initialConditionFormData: ConditionFormData | undefined;
  qualityControlFormData: ConditionFormData | undefined;
  productAttributes: ProductAttribute[] | undefined;
  requestView: RequestContextData['view'];
  articleState?: Omit<ArticleContextData['state'], 'errors'>;
}) {
  const initialConditionArticleConditionForm =
    initialConditionFormData?.articleConditionForms?.find(
      (articleConditionForm) => !articleConditionForm.completedAt
    );

  const qualityControlArticleConditionForm = qualityControlFormData?.articleConditionForms?.find(
    (articleConditionForm) => !articleConditionForm.completedAt
  );

  return {
    details: {
      product:
        !article.uniqueArticle && !article.product && article.productAttributeValues.length === 0,
      productAttributes: productAttributes.map((productAttribute) => ({
        id: productAttribute.id,
        missingValue:
          productAttribute.enabled &&
          productAttribute.required &&
          isProductAttributeValueInputEnabled({
            article,
            productAttribute,
          }) &&
          !article.productAttributeValues.find((selectedValue) =>
            productAttribute.productAttributeValues.some(
              (currentProductAttributeValue) => currentProductAttributeValue.id === selectedValue.id
            )
          ),
      })),
      missingBrand: !article.product && !article.brandId,
      otherBrandEmpty: !!article.brand?.isOtherBrand && !article.otherBrandName,
      missingColor: hasColorOptions({ article }) && !article.color,
      missingSize: hasSizeOptions({ article }) && !article.size,
      missingWeight: !article.weight,
      missingArticlePhoto: !article.exchangedFromId && !getArticlePhoto(article),
      missingArticlePhotoForRefashion:
        !getArticlePhoto(article) &&
        article.snapshot.articleActions.some(
          (action) => getRefashionStatus(action.organizationPrice) === 'applied'
        ),
      missingEstimatedValue: !article.data.estimatedValue,
    },
    customs:
      !!article?.areCustomsExpected &&
      isMissingCustomsData(article, requestView.customs.type) &&
      hasPermission(currentSession, 'edit_customs_data', [
        {
          organizationId: article.organizationId,
        },
        article.workshopId
          ? {
              entityId: article.workshopId,
            }
          : null,
      ]),
    warranty: {
      missingPurchaseDate: !article.purchaseDate,
      invalidPurchaseDate: !!article.purchaseDate && parseISO(article.purchaseDate) > new Date(),
      missingPurchaseLocation: !article.purchaseLocation,
      missingPurchaseCountry: !article.purchaseCountry,
      missingPurchaseChannel: !article.purchaseChannel,
      missingOrderNumber: !article.orderNumber,
      missingProofOfPurchase: proofOfPurchaseMedia.length === 0,
      missingProofOfPurchaseOrOrderNumber:
        !article.orderNumber && proofOfPurchaseMedia.length === 0,
      missingProofOfAuthenticity: proofOfAuthenticityMedia.length === 0,
      missingOverrideReason: !!article.warrantyOverride && !article.warrantyOverrideReason,
    },
    condition: {
      questions: (initialConditionFormData?.conditionForm?.questions ?? []).map((question) => ({
        ...computeQuestionErrors({
          question,
          articleConditionForm: initialConditionArticleConditionForm,
        }),
        subQuestion: computeQuestionErrors({
          question: getSubQuestion({
            question,
            articleConditionForm: initialConditionArticleConditionForm,
          }),
          articleConditionForm: initialConditionArticleConditionForm,
        }),
      })),
    },
    defects: {
      noDefects: article.snapshot.articleDefects.length === 0,
      defects: article.snapshot.articleDefects.map((defect) => ({
        id: defect.id,
        missingDescription: isDefectCustom(defect) && !defect.customDescription,
        missingPhotos: defect.media.length === 0,
        missingActions:
          article.snapshot.serviceChoice === 'care-repair' &&
          defect.toBeRepaired &&
          defect.articleActionToArticleDefects?.length === 0,
        missingContext: !defect.context,
        missingExceptionalCoverageReason:
          defect.exceptionalBrandResponsibilityCoverageRequested &&
          !defect.exceptionalBrandResponsibilityCoverageReason,
      })),
    },
    services: {
      choice: {
        noChoice: !article.snapshot.serviceChoice,
      },
      actions: {
        noActions: article.snapshot.articleActions.length === 0,
        actions: article.snapshot.articleActions.map((action) => ({
          id: action.id,
          missingDescription: isActionCustom(action) && !action.customDescription,
          missingPhotos: action.media.length === 0,
          missingRefashionPhoto:
            getRefashionStatus(action.organizationPrice) === 'applied' && action.media.length === 0,
          missingWorkshopPrice: !action.workshopPrice?.amountPerEntity?.find(
            (amount) => amount.entityId === article.workshopId
          ),
          missingOrganizationPrice: !action.organizationPrice?.amountPerEntity?.[0],
          missingContext: !action.context,
          workshopPriceUpdateInProgress: !!articleState?.priceUpdates.updatesInProgress.find(
            (priceUpdate) => priceUpdate.actionId === action.id && priceUpdate.type === 'workshop'
          ),
          organizationPriceUpdateInProgress: !!articleState?.priceUpdates.updatesInProgress.find(
            (priceUpdate) =>
              priceUpdate.actionId === action.id && priceUpdate.type === 'organization'
          ),
          spareParts: action.spareParts.map((sparePart) => ({
            id: sparePart.id,
            quantityIsNull: sparePart.quantity === 0,
            missingWorkshopPrice: !sparePart.workshopPrice?.amountPerEntity?.find(
              (amount: { entityId?: string }) => amount.entityId === article.workshopId
            ),
            missingOrganizationPrice: !sparePart.organizationPrice?.amountPerEntity?.[0],
            workshopPriceUpdateInProgress: !!articleState?.priceUpdates.updatesInProgress.find(
              (priceUpdate) =>
                priceUpdate.actionId === action.id &&
                priceUpdate.sparePartId === sparePart.id &&
                priceUpdate.type === 'workshop'
            ),
            organizationPriceUpdateInProgress: !!articleState?.priceUpdates.updatesInProgress.find(
              (priceUpdate) =>
                priceUpdate.actionId === action.id &&
                priceUpdate.sparePartId === sparePart.id &&
                priceUpdate.type === 'organization'
            ),
          })),
          additionalInfo: [
            ...(action.actionTypeOrganization?.additionalInfoConfig ?? []),
            ...(action.packActionTypeOrganization?.additionalInfoConfig ?? []),
          ].map((config) => ({
            id: config.id,
            missingValue:
              config.enabled &&
              config.required &&
              !action.additionalInfo?.find(
                ({ actionTypeOrganizationAdditionalInfoConfigId }) =>
                  actionTypeOrganizationAdditionalInfoConfigId === config.id
              ),
          })),
        })),
      },
      exchange: {
        missingExchangeType: !article.snapshot.articleExchange?.type,
        missingReplacementProduct: !article.snapshot.articleExchange?.replacementProductId,
        missingReplacementUniqueArticle:
          !article.snapshot.articleExchange?.replacementUniqueArticleId,
        missingReplacementProductColor:
          hasColorOptions({ article, isReplacementProduct: true }) &&
          !article.snapshot.articleExchange?.replacementColor,
        missingReplacementProductSize:
          hasSizeOptions({ article, isReplacementProduct: true }) &&
          !article.snapshot.articleExchange?.replacementSize,
        missingWorkshopPrice:
          !!article.snapshot.articleExchange?.replacementProduct &&
          !article.snapshot.articleExchange?.workshopPrice?.amountPerEntity?.find(
            (amount: { entityId?: string }) => amount.entityId === article.workshopId
          ),
        missingOrganizationPrice:
          !!article.snapshot.articleExchange?.replacementProduct &&
          !article.snapshot.articleExchange?.organizationPrice?.amountPerEntity?.[0],
        missingStockLocation: !article.snapshot.articleExchange?.stockLocationId,
        workshopPriceUpdateInProgress: !!articleState?.priceUpdates.updatesInProgress.find(
          (priceUpdate) =>
            priceUpdate.articleExchangeId === article.snapshot.articleExchange?.id &&
            priceUpdate.type === 'workshop'
        ),
        organizationPriceUpdateInProgress: !!articleState?.priceUpdates.updatesInProgress.find(
          (priceUpdate) =>
            priceUpdate.articleExchangeId === article.snapshot.articleExchange?.id &&
            priceUpdate.type === 'organization'
        ),
      },
      dispatch: {
        noWorkshopSelected: !article?.workshopId,
      },
    },
    thresholds: {
      organization:
        !article.organizationRequireQuote &&
        article.manualOrganizationThresholdAmount !== 0 &&
        !article.manualOrganizationThresholdAmount &&
        article.defaultOrganizationThresholdAmount !== 0 &&
        !article.defaultOrganizationThresholdAmount,
      client:
        !article.clientRequireQuote &&
        article.manualClientThresholdAmount !== 0 &&
        !article.manualClientThresholdAmount &&
        article.defaultClientThresholdAmount !== 0 &&
        !article.defaultClientThresholdAmount,
    },
    qualityControl: {
      noAnswerer: qualityControlArticleConditionForm?.answerer === null,
      questions: (qualityControlFormData?.conditionForm?.questions ?? []).map((question) => ({
        ...computeQuestionErrors({
          question,
          articleConditionForm: qualityControlArticleConditionForm,
        }),
        subQuestion: computeQuestionErrors({
          question: getSubQuestion({
            question,
            articleConditionForm: qualityControlArticleConditionForm,
          }),
          articleConditionForm: qualityControlArticleConditionForm,
        }),
      })),
      repairedPhoto: qualityControlArticleConditionForm?.media.length === 0,
      actionQuestions: article.snapshot.articleActions.map((action) => ({
        ...computeActionsQuestionErrors({
          action,
          articleConditionForm: qualityControlArticleConditionForm,
        }),
      })),
    },
  };
}

function computeQuestionErrors({
  question,
  articleConditionForm,
}: {
  question: null | ConditionForm['questions'][number];
  articleConditionForm?: ArticleConditionForm;
}) {
  const articleConditions = question
    ? articleConditionForm?.articleConditions?.filter(
        (articleCondition) => articleCondition.questionId === question?.id
      )
    : undefined;

  const selectedAnswerId = articleConditions?.[0]?.answerId ?? null;

  const hasBlockingAnswer = !!question?.answers?.find((answer) => selectedAnswerId === answer.id)
    ?.makesQualityControlKO;

  return {
    id: question?.id ?? '',
    noAnswers: articleConditions?.length === 0,
    missingContext:
      question?.type === 'radio' && hasBlockingAnswer && !articleConditions?.[0]?.context,
    missingPhoto:
      question?.type === 'radio' && hasBlockingAnswer && articleConditions?.[0]?.media.length === 0,
    missingText:
      question?.type === 'text-and-select' &&
      (!articleConditions?.[0]?.answerData ||
        !('text' in articleConditions[0].answerData) ||
        !articleConditions[0].answerData.text),
    missingSelect:
      question?.type === 'text-and-select' &&
      (!articleConditions?.[0]?.answerData ||
        !('select' in articleConditions[0].answerData) ||
        !articleConditions[0].answerData.select),
    answers: (articleConditions ?? []).map((articleCondition) => ({
      missingSelect:
        question?.type === 'multi-quantifiable-select' &&
        !('answerId' in articleCondition && !!articleCondition.answerId),
    })),
  };
}

function computeActionsQuestionErrors({
  action,
  articleConditionForm,
}: {
  action: null | { id: string };
  articleConditionForm?: ArticleConditionForm;
}) {
  const articleCondition = articleConditionForm?.articleConditions.find(
    (articleCondition) => articleCondition.articleActionId === action?.id
  );

  return {
    id: action?.id ?? '',
    missingContext:
      !!articleConditionForm &&
      articleCondition?.answerData?.yesNoRadioValue === 'no' &&
      !articleCondition?.context,
    missingPhoto:
      !!articleConditionForm &&
      articleCondition?.answerData?.yesNoRadioValue === 'no' &&
      !articleCondition?.media.length,
    noAnswer: !!articleConditionForm && !articleCondition?.answerData?.yesNoRadioValue,
  };
}

function isProductAttributeValueInputEnabled({
  article,
  productAttribute,
}: {
  article: ArticleWithRelations;
  productAttribute: ProductAttribute;
}) {
  if (!productAttribute.enabled) {
    return false;
  }

  const isAlreadyFilledByProduct = !!article.product?.productAttributeValues.find((value) =>
    productAttribute.productAttributeValues.some(
      (productAttributeValue) => productAttributeValue.id === value.id
    )
  );

  if (isAlreadyFilledByProduct) {
    return false;
  }

  if (!productAttribute.parentProductAttributeValueId) {
    return true;
  }

  return article.productAttributeValues.some(
    (value) => value.id === productAttribute.parentProductAttributeValueId
  );
}

export type ArticleErrors = ReturnType<typeof computeArticleErrors>;
export type ArticleErrorsFilter = ErrorsFilter<ArticleErrors>;
export type FilteredArticleErrors = FilteredErrors<ArticleErrors>;

type Errors = { [key: string]: string | boolean | Errors | Errors[] };
type ErrorsFilter<T extends Errors> = {
  [K in Exclude<keyof T, 'id'>]?: T[K] extends Errors
    ? ErrorsFilter<T[K]>
    : T[K] extends Array<infer U>
      ? U extends Errors
        ? ErrorsFilter<U> | (ErrorsFilter<U> & { id: string })[]
        : never
      : boolean;
};
type FilteredErrors<T extends Errors> = {
  [K in Exclude<keyof T, 'id'>]?: T[K] extends Errors
    ? FilteredErrors<T[K]>
    : T[K] extends Array<infer U>
      ? U extends Errors
        ? (FilteredErrors<U> & { id: string })[]
        : never
      : boolean;
} & {
  hasError: boolean;
};

export function recursivelyFilterErrors<T extends Errors>(
  errors: T,
  errorsFilter: ErrorsFilter<T>
): FilteredErrors<T> {
  let hasError = false;
  const filteredErrors: any = {};

  for (const key in errorsFilter) {
    if (typeof errors[key] === 'boolean') {
      if (errorsFilter[key] === true && errors[key] === true) {
        filteredErrors[key] = true;
        hasError = true;
      }
    }

    if (typeof errors[key] === 'object') {
      if (Array.isArray(errors[key])) {
        filteredErrors[key] = errors[key].map(({ id, ...subErrors }) => {
          if (Array.isArray(errorsFilter[key])) {
            return {
              id,
              ...recursivelyFilterErrors(
                subErrors,
                (errorsFilter[key].find((filter) => filter.id === id) as ErrorsFilter<T>) ?? {}
              ),
            };
          }

          return {
            id,
            ...recursivelyFilterErrors(subErrors, errorsFilter[key] as ErrorsFilter<T>),
          };
        });
        hasError = filteredErrors[key].some((error: any) => error.hasError as boolean) || hasError;
      } else {
        filteredErrors[key] = recursivelyFilterErrors(
          errors[key],
          errorsFilter[key] as ErrorsFilter<T>
        );
        hasError = filteredErrors[key].hasError || hasError;
      }
    }
  }

  return {
    ...filteredErrors,
    hasError,
  } as FilteredErrors<T>;
}
